import clsx from "clsx";
import { Checkbox, FormControl, FormControlLabel, FormHelperText, makeStyles, Radio, RadioGroup } from "@material-ui/core";

const useStyles = makeStyles({
    root: {
        flexGrow: 1,
        border: (props) => (props.error ? "1px solid #fd397a" : "1px solid #374151"),
        borderRadius: (props) => (props.radius8 ? 8 : 4),
        padding: "31px 24px",
        margin: 0,
        display: "flex",
        flex: (props) => (props.fullwidthitem ? "100%" : props.wrap3 ? "1 1 calc(33.33% - 24px)" : "1 1 calc(50% - 24px)"),
        minWidth: 210,
        flexDirection: (props) => (props.isPlan ? "column" : "row"),
        gap: (props) => (props.isPlan ? 24 : undefined),
        alignItems: (props) => (props.isPlan ? "flex-start" : undefined),
    },
    tinyRoot: {
        flexGrow: 1,
        border: (props) => (props.error ? "1px solid #fd397a" : "1px solid #374151"),
        borderRadius: (props) => (props.radius8 ? 8 : 4),
        padding: "24px 20px",
        margin: 0,
        display: "flex",
        flex: (props) => (props.fullwidthitem ? "100%" : props.wrap3 ? "1 1 calc(33.33% - 24px)" : "1 1 calc(50% - 24px)"),
        minWidth: 210,
    },
    veryTinyRoot: {
        flexGrow: 1,
        border: (props) => (props.error ? "1px solid #fd397a" : "1px solid #374151"),
        borderRadius: (props) => (props.radius8 ? 8 : 4),
        padding: 16,
        margin: 0,
        display: "flex",
        flex: (props) => (props.fullwidthitem ? "100%" : props.wrap3 ? "1 1 calc(33.33% - 24px)" : "1 1 calc(50% - 24px)"),
        minWidth: 210,
    },
    label: {
        fontSize: 24,
        fontWeight: 500,
        lineHeight: "32px",
        fontFamily: "Inter",
        color: "#fff !important",
        margin: 0,
        marginLeft: 24,
        width: "100%",
        alignSelf: "flex-start",
    },
    planLabel: {
        color: "#fff !important",
        margin: 0,
        marginLeft: 2,
        width: "100%",
        alignSelf: "flex-start",
    },
    tinyLabel: {
        fontSize: 18,
        fontWeight: 500,
        lineHeight: "28px",
        fontFamily: "Inter",
        color: "#fff !important",
        margin: 0,
        marginLeft: 20,
        width: "100%",
        alignSelf: "flex-start",

        "&.Mui-disabled": {
            color: "#374151 !important",
        },
    },
    control: {
        color: "#7F59D9",
    },
});

const radioStyles = makeStyles({
    root: {
        "&:hover": {
            backgroundColor: "transparent",
        },
    },
    icon: {
        borderRadius: "50%",
        width: (props) => (props.tiny ? 10 : 14),
        height: (props) => (props.tiny ? 10 : 14),
        backgroundColor: "unset",
        outline: "1px solid #374151",
        outlineOffset: 5,
        "$root.Mui-focusVisible &": {
            outline: (props) => `${props.tiny ? 6 : 7}px solid #7F59D9`,
            outlineOffset: 0,
        },
        "input:hover:not(:disabled) ~ &": {
            outline: (props) => `${props.tiny ? 6 : 7}px solid #7F59D9`,
            outlineOffset: 0,
        },
    },
    checkedIcon: {
        outline: (props) => `${props.tiny ? 6 : 7}px solid #7F59D9`,
        outlineOffset: 0,
    },
});

const checkboxStyles = makeStyles({
    root: {
        "& .MuiSvgIcon-root": {
            fontSize: 25,
        },
        alignSelf: "flex-start",
        color: "#374151",
        padding: "9px 0px",
        borderRadius: 8,
        "&.Mui-checked": {
            color: "#7F59D9",
        },
    },
});

/**
 * @see https://v4.mui.com/components/radio-buttons/
 */
const CustomRadioGroupButtons = ({
    items = [
        {
            value: "value",
            label: "label",
            disabled: false,
            description: null,
            rightHeader: null,
            contentOnSelect: null,
            onChange: () => {},
            metadata: {},
        },
    ],
    error,
    helperText,
    value,
    name,
    variant = "radio", // radio | checkbox | tiny | veryTiny | plan
    fullWidthItem = false,
    disabled,
    wrap3,
    radius8 = false,
    gap16 = false,
}) => {
    const classes = useStyles({ fullwidthitem: fullWidthItem, error, wrap3, radius8, isPlan: variant === "plan" });
    const radioClasses = radioStyles({ tiny: variant === "tiny" || variant === "veryTiny" });
    const checkboxClasses = checkboxStyles();

    const falseItemsToRender = items.length % 2;

    return (
        <FormControl fullWidth>
            <RadioGroup style={{ display: "flex", flexDirection: "row", gap: gap16 ? 16 : 24 }} value={value} name={name}>
                {items.map((item, index) => {
                    const isCheckboxVariant = variant === "checkbox";

                    const label = isCheckboxVariant ? (
                        <div className="flex-row space-between gap-8 wrap align-items-center">
                            <span className="tt-18 tt-semibold color-gray-100">{item.label}</span>

                            <span className="tt-14 tt-regular color-gray-300">{item.rightHeader}</span>
                        </div>
                    ) : (
                        item.label
                    );

                    const selected = value === item.value;

                    return (
                        <>
                            <FormControlLabel
                                key={`rdg${index}`}
                                value={item.value}
                                onChange={item.onChange}
                                disabled={disabled || item.disabled}
                                control={
                                    (["radio", "tiny", "veryTiny", "plan"].includes(variant) && (
                                        <Radio key={`r${index}`} color="primary" className={radioClasses.root} icon={<span className={radioClasses.icon} />} checkedIcon={<span className={item.disabled && !selected ? radioClasses.icon : clsx(radioClasses.icon, radioClasses.checkedIcon)} />} />
                                    )) ||
                                    (isCheckboxVariant && <Checkbox key={`c${index}`} color="default" className={checkboxClasses.root} checked={value?.includes(item.value)} />)
                                }
                                label={
                                    item.description ? (
                                        <div className="d-flex flex-column gap-8">
                                            {label}
                                            {item.description}
                                        </div>
                                    ) : (
                                        label
                                    )
                                }
                                classes={{
                                    root: variant === "veryTiny" ? classes.veryTinyRoot : variant === "tiny" ? classes.tinyRoot : classes.root,
                                    label: variant === "tiny" || variant === "veryTiny" ? classes.tinyLabel : variant === "plan" ? classes.planLabel : classes.label,
                                }}
                                style={{ border: selected ? "1px solid #7F59D9" : undefined }}
                            />

                            {selected ? item.contentOnSelect : null}
                        </>
                    );
                })}

                {fullWidthItem || wrap3
                    ? null
                    : Array(falseItemsToRender)
                          .fill(null)
                          .map((_, index) => <div key={`f${index}`} className={classes.root} style={{ border: "unset" }}></div>)}
            </RadioGroup>
            {error && (
                <FormHelperText error style={{ textAlign: "center" }}>
                    {helperText}
                </FormHelperText>
            )}
        </FormControl>
    );
};

export default CustomRadioGroupButtons;
